import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from './button.js';
import { Textarea } from './textarea.js';
import { CheckIcon } from './icons.js';
import { ApiError } from '../../features/posts/api.js';
import { quickStatusErrorMessage } from '../../features/posts/quick-status-errors.js';

/** Throw from `onUndo` to show this exact message in the toast (e.g. a partial batch undo). */
export class UndoFailure extends Error {}

/** How long the toast stays before it closes on its own. */
export const UNDO_TOAST_MS = 10_000;

export interface UndoToastOptions {
  message: string;
  onUndo: () => void | Promise<void>;
  /** When set, the toast offers an inline note field that calls this with the trimmed text. */
  onAddNote?: (text: string) => void | Promise<void>;
}

interface ActiveToast extends UndoToastOptions {
  /** Changes on every show(), so a replacement restarts the timer and the note state. */
  id: number;
}

interface UndoToastApi {
  show: (opts: UndoToastOptions) => void;
}

const UndoToastContext = createContext<UndoToastApi | null>(null);

/** App-root provider — mount once near the top of the tree. One toast at a time. */
export function UndoToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const seq = useRef(0);
  const show = useCallback((opts: UndoToastOptions) => {
    seq.current += 1;
    setToast({ ...opts, id: seq.current });
  }, []);
  // Scoped to the toast that asked: a late close must not wipe a newer toast.
  const close = useCallback((id: number) => setToast((cur) => (cur?.id === id ? null : cur)), []);
  const api = useMemo(() => ({ show }), [show]);

  return (
    <UndoToastContext.Provider value={api}>
      {children}
      {toast && <UndoToast key={toast.id} toast={toast} onClose={() => close(toast.id)} />}
    </UndoToastContext.Provider>
  );
}

export function useUndoToast(): UndoToastApi {
  const ctx = useContext(UndoToastContext);
  if (!ctx) throw new Error('useUndoToast must be used within <UndoToastProvider>');
  return ctx;
}

function UndoToast({ toast, onClose }: { toast: ActiveToast; onClose: () => void }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  // Keep the latest onClose without restarting the timer on every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // The timer pauses while the note field is open, so typing is never cut off;
  // closing the field restarts the full countdown.
  useEffect(() => {
    if (noteOpen || undoing) return;
    const t = setTimeout(() => closeRef.current(), UNDO_TOAST_MS);
    return () => clearTimeout(t);
  }, [noteOpen, undoing]);

  const undo = async () => {
    setUndoing(true);
    setUndoError(null);
    try {
      await toast.onUndo();
      onClose();
    } catch (err) {
      setUndoError(
        err instanceof UndoFailure
          ? err.message
          : err instanceof ApiError
            ? quickStatusErrorMessage(err, { undo: true })
            : 'A visszavonás nem sikerült',
      );
      setUndoing(false);
    }
  };

  const saveNote = async () => {
    const text = note.trim();
    if (!text || !toast.onAddNote) return;
    setSaving(true);
    setNoteError(false);
    try {
      await toast.onAddNote(text);
      onClose();
    } catch {
      setNoteError(true);
      setSaving(false);
    }
  };

  const cancelNote = () => {
    if (saving) return;
    setNoteOpen(false);
    setNote('');
    setNoteError(false);
  };

  // bottom-36 / z-[45]: above the BulkBar (fixed bottom-6 z-40; with its summary row it is ~116px tall, so the toast starts at 144px), below dialogs (z-50) and the AI toast (z-60).
  return (
    <div className="pointer-events-none fixed bottom-36 left-1/2 z-[45] w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto flex flex-col gap-2 rounded-xl bg-[var(--brand-700)] px-4 py-3 text-meta font-medium text-white shadow-lg"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <CheckIcon size={14} className="flex-shrink-0" />
            <span className="min-w-0 break-words">{toast.message}</span>
          </span>
          <Button size="sm" variant="secondary" disabled={undoing} onClick={() => void undo()}>
            Visszavonás
          </Button>
          {toast.onAddNote && !noteOpen && (
            <Button size="sm" variant="secondary" onClick={() => setNoteOpen(true)}>
              Megjegyzés hozzáadása
            </Button>
          )}
        </div>
        {undoError && <span>{undoError}</span>}
        {noteOpen && (
          <div className="flex flex-col gap-2">
            <Textarea
              aria-label="Megjegyzés"
              rows={2}
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  cancelNote();
                }
              }}
              className="min-h-0"
            />
            {noteError && <span>Nem sikerült a mentés</span>}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-white"
                disabled={saving}
                onClick={cancelNote}
              >
                Mégse
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={saving || note.trim() === ''}
                onClick={() => void saveNote()}
              >
                Mentés
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
